import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Link from '@rad/components/utility/link';
import Modal from '@rad/components/ui/modal';
import { getRouteParams, useRouting } from '@rad/hooks/useRouting';
import { withDone } from '@rad/utility/http';
import useAuth from '../hooks/useAuth';
import { pathToURL } from '../common/routes/urls/_';
import { generalUrls } from '../common/routes/urls/general';
import { getSavedPropertyIds, saveProperty, unsaveProperty } from '../common/network/saved-property';
const SavedPropertiesContext = createContext({
    isLoaded: true,
    isSaved: () => false,
    toggle: () => { },
});
export const useSavedProperties = () => useContext(SavedPropertiesContext);
const withMember = (ids, id, member) => {
    const next = new Set(ids);
    if (member)
        next.add(id);
    else
        next.delete(id);
    return next;
};
// Which properties the signed-in user has saved, read once per sign-in so every heart on every
// screen agrees. A heart flips at once and flips back if the server refuses; the failure itself is
// already toasted by the app's response interceptor. Until the list arrives a heart does nothing,
// since there is no telling yet which way it should flip. A signed-out visitor is asked to sign in.
export const SavedPropertiesProvider = (props) => {
    const [auth] = useAuth();
    const [routeCurrent] = useRouting();
    const [ids, setIds] = useState(null);
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    useEffect(() => {
        if (!auth.isAuthenticated) {
            setIds(new Set());
            return;
        }
        setIds(null);
        let isCurrent = true;
        withDone(getSavedPropertyIds)()
            .then(xs => { if (isCurrent)
            setIds(new Set(xs)); })
            .catch(() => { if (isCurrent)
            setIds(new Set()); });
        return () => { isCurrent = false; };
    }, [auth.isAuthenticated]);
    // Following the prompt to sign in, or leaving the page any other way, puts it away.
    useEffect(() => {
        setIsPromptOpen(false);
    }, [routeCurrent.raw]);
    const toggle = useCallback((propertyId) => {
        if (!auth.isAuthenticated) {
            setIsPromptOpen(true);
            return;
        }
        if (ids === null)
            return;
        const saving = !ids.has(propertyId);
        setIds(prev => prev && withMember(prev, propertyId, saving));
        const request = saving ? saveProperty(propertyId) : unsaveProperty(propertyId);
        request.catch(() => setIds(prev => prev && withMember(prev, propertyId, !saving)));
    }, [auth.isAuthenticated, ids]);
    const ctx = useMemo(() => ({
        isLoaded: ids !== null,
        isSaved: (propertyId) => !!(ids === null || ids === void 0 ? void 0 : ids.has(propertyId)),
        toggle,
    }), [ids, toggle]);
    // Back to this page, query and all, once they have signed in.
    const search = getRouteParams().toString();
    const next = `?next=${encodeURIComponent(pathToURL(routeCurrent.pieces) + (search ? `?${search}` : ''))}`;
    return (_jsxs(SavedPropertiesContext.Provider, { value: ctx, children: [props.children, _jsx(Modal, { isOpen: isPromptOpen, title: "Save homes you love", onClose: () => setIsPromptOpen(false), footer: _jsxs(_Fragment, { children: [_jsx(Link, { href: `${generalUrls.signUp.getURL()}${next}`, className: "btn btn-outline-secondary", children: "Create an account" }), _jsx(Link, { href: `${generalUrls.login.getURL()}${next}`, className: "btn btn-primary", children: "Sign in" })] }), children: _jsx("p", { className: "mb-0", children: "Sign in or create a free account to save properties. Your saved homes stay with your account, so you can find them again from any device." }) })] }));
};
